import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { BoxIcon, XIcon } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { NodeType, ModeType } from '@/types/graph'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/i18n'
import { useTaxonomy } from './TaxonomyContext'

interface ControlPanelProps {
  mode: ModeType
  onConfigChange: (config: { type: NodeType; label?: string; note?: string; pnid_level_i?: string; pnid_level_ii?: string; pnid_level_iii?: string; detectConnections?: boolean }) => void
  isOpen?: boolean
  onClose?: () => void
  hasActiveTooltip?: boolean
  hasPendingPatch?: boolean
  pendingConnectedCount?: number
  onSaveToGraph?: () => void
  onDiscardDetection?: () => void
  hasDetectedBoundary?: boolean
  onUseDetectedBoundary?: () => void
}

/** The Level I a fresh selection starts on, per discipline. Each is that
 *  sheet's most-drawn symbol branch, so the commonest add needs one fewer click. */
const DEFAULT_LEVEL_I: Record<string, string> = {
  pnid: 'Equipment',
  sld: 'Electrical',
}

// resolveNodeType returns the string union 'symbol' | 'tag' | 'connection',
// whose members are exactly the NodeType enum's values. This map converts to
// the enum without an unsafe cast.
const NODE_TYPE_BY_STRING: Record<'symbol' | 'tag' | 'connection', NodeType> = {
  symbol: NodeType.Symbol,
  tag: NodeType.Tag,
  connection: NodeType.Connection,
}

const ControlPanel = ({
  mode,
  onConfigChange,
  isOpen: propIsOpen,
  onClose,
  hasActiveTooltip,
  hasPendingPatch,
  pendingConnectedCount,
  onSaveToGraph,
  onDiscardDetection,
  hasDetectedBoundary,
  onUseDetectedBoundary,
}: ControlPanelProps) => {
  const { t } = useTranslation()
  const taxonomy = useTaxonomy()
  const [isOpen, setIsOpen] = useState(propIsOpen ?? false)

  const [levelI, setLevelI] = useState<string>(
    DEFAULT_LEVEL_I[taxonomy.discipline] ?? taxonomy.levelIValues[0] ?? '',
  )
  const [levelII, setLevelII] = useState<string>('')
  const [levelIII, setLevelIII] = useState<string>('')
  const [label, setLabel] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [detectConnections, setDetectConnections] = useState<boolean>(true)

  const levelIIOptions = taxonomy.getLevelII(levelI)
  const levelIIIOptions = levelII ? taxonomy.getLevelIII(levelI, levelII) : []
  const isConnectionMode = taxonomy.resolveNodeType(levelI, levelII) === NodeType.Connection

  useEffect(() => {
    if (propIsOpen !== undefined) setIsOpen(propIsOpen)
  }, [propIsOpen])

  useEffect(() => {
    if (mode === ModeType.Create && !hasActiveTooltip) {
      setIsOpen(true)
    } else if (mode !== ModeType.Drag) {
      // Only close when switching to a non-pan mode (e.g. Select).
      // Drag/pan should keep the panel open so users can pan while adding nodes.
      setIsOpen(false)
    }
  }, [mode, hasActiveTooltip])

  // Notify parent whenever config changes. We always fire — even when levelII
  // is empty — so that switching away from a connection selection resets the
  // parent's addNodeConfig back to a non-connection state and unblocks node
  // creation via drag.
  useEffect(() => {
    onConfigChange({
      type: NODE_TYPE_BY_STRING[taxonomy.resolveNodeType(levelI, levelII, levelIII)],
      label: label || undefined,
      note: note || undefined,
      pnid_level_i: levelI,
      pnid_level_ii: levelII || undefined,
      pnid_level_iii: levelIII || undefined,
      detectConnections,
    })
  }, [levelI, levelII, levelIII, label, note, detectConnections, onConfigChange])

  const handleClose = () => {
    setIsOpen(false)
    onClose?.()
  }

  const handleLevelIChange = (value: string) => {
    setLevelI(value)
    setLevelII('')
    setLevelIII('')
    setLabel('')
    setNote('')
  }

  const handleLevelIIChange = (value: string) => {
    setLevelII(value)
    setLevelIII('')
    setLabel('')
    setNote('')
  }

  if (mode !== ModeType.Create && !isOpen) return null

  return (
    <>
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: isOpen ? 0 : '100%' }}
        transition={{ ease: 'easeInOut', duration: 0.3 }}
        className="absolute top-0 right-0 h-full border-t w-3/12 bg-white dark:bg-dark-card shadow-xl border-l border-gray-200 dark:border-dark-border z-50 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-border shrink-0">
          <div className="flex items-center gap-3">
            <BoxIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text py-1">
              {t('data_catalog:control_panel.title')}
            </h3>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClose}>
            <XIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex flex-col min-h-0 flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-dark-text">
                {t('data_catalog:control_panel.node_configuration')}
              </h4>

              <div className="space-y-4">
                {/* Level I — Category */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('data_catalog:control_panel.level_i.label')}
                  </label>
                  <Select value={levelI} onValueChange={handleLevelIChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('data_catalog:control_panel.level_i.placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {taxonomy.levelIValues.map((val) => (
                        <SelectItem key={val} value={val}>{val}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Level II — Subcategory */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('data_catalog:control_panel.level_ii.label')}
                  </label>
                  <Select value={levelII} onValueChange={handleLevelIIChange} disabled={!levelI}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('data_catalog:control_panel.level_ii.placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {levelIIOptions.map((val) => (
                        <SelectItem key={val} value={val}>{val}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Level III — Type (always shown, disabled when no options) */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('data_catalog:control_panel.level_iii.label')}
                  </label>
                  <Select
                    value={levelIII}
                    onValueChange={setLevelIII}
                    disabled={!levelII || levelIIIOptions.length === 0}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={
                        levelII && levelIIIOptions.length === 0
                          ? t('graph_editor:nodePanel.notApplicable', { defaultValue: 'N/A' })
                          : t('data_catalog:control_panel.level_iii.placeholder')
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {levelIIIOptions.map((val) => (
                        <SelectItem key={val} value={val}>{val}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tag — always shown for all node types */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('graph_editor:nodePanel.label', { defaultValue: 'Tag' })}</label>
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder={t('graph_editor:controlPanel.labelPlaceholder', { defaultValue: 'e.g. V-101, FIC-201, 69TE1120A' })}
                    className="w-full"
                  />
                </div>

                {/* Note — always shown */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('data_catalog:control_panel.note.label')}
                  </label>
                  <Input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('data_catalog:control_panel.note.placeholder')}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Detect Connections Toggle */}
              {!isConnectionMode && (
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    id="detect-connections"
                    checked={detectConnections}
                    onChange={(e) => setDetectConnections(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="detect-connections" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('graph_editor:controlPanel.detectConnections', { defaultValue: 'Detect connections' })}
                  </label>
                </div>
              )}

              {/* Use detected boundary button — shown after click-to-detect */}
              {hasDetectedBoundary && !hasPendingPatch && (
                <div className="pt-2">
                  <Button
                    size="sm"
                    onClick={onUseDetectedBoundary}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {t('graph_editor:controlPanel.useDetectedBoundary', { defaultValue: 'Use detected boundary' })}
                  </Button>
                </div>
              )}

              {/* Save / Discard buttons — shown after detection */}
              {hasPendingPatch && (
                <div className="space-y-2 pt-2">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('graph_editor:controlPanel.connectionsDetected', { count: pendingConnectedCount, defaultValue: '{{count}} connection(s) detected' })}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={onSaveToGraph}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      {t('graph_editor:controlPanel.saveToGraph', { defaultValue: 'Save to Graph' })}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onDiscardDetection}
                      className="flex-1"
                    >
                      {t('graph_editor:controlPanel.discard', { defaultValue: 'Discard' })}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <hr className="border-gray-200 dark:border-dark-border" />

            {/* Instructions */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-dark-text">
                {t('data_catalog:control_panel.instructions.title')}
              </h4>
              {isConnectionMode ? (
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <p>{t('data_catalog:control_panel.instructions.connection.start')}</p>
                  <p>{t('data_catalog:control_panel.instructions.connection.end')}</p>
                  <p>{t('data_catalog:control_panel.instructions.connection.note')}</p>
                </div>
              ) : (
                <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                  <p>{t('data_catalog:control_panel.instructions.step1')}</p>
                  <p>{t('data_catalog:control_panel.instructions.step2')}</p>
                  <p>{t('data_catalog:control_panel.instructions.step3')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </>
  )
}

export default ControlPanel
